// MS-6583: name the request, and the page state that explains it, when a fetch dies at the
// network layer.
//
// A network-level rejection ("Failed to fetch") carries no URL and no cause, and the vendored
// Bugsnag v3.0.6 records no network breadcrumbs — so the one event bugsnagNetworkNoise lets
// through per window used to say nothing actionable. fetchM attaches this object to every
// fetch() rejection as `error.mhubNetwork`; the noise filter merges it into the event's
// `network` tab. Every field is best-effort (a diagnostics failure must never turn one error
// into two) and SIZES ONLY — never a header or cookie value.
import { isNetworkFailureMessage } from './bugsnagNetworkNoise';

// nginx's large_client_header_buffers default. The API proxy tier answers any header line at
// or above this with a CORS-less 400, which the browser reports as exactly this error (DV-880).
export const BIG_HEADER_LINE_BYTES = 8 * 1024;

// Resource Timing start times and performance.now() come from the same clock, but a failed
// request's entry can be stamped a hair before the caller's own reading.
const START_TOLERANCE_MS = 5;

function navigationEntry(perf) {
  try {
    if (!perf || typeof perf.getEntriesByType !== 'function') return null;
    const entries = perf.getEntriesByType('navigation');
    return entries && entries[0] ? entries[0] : null;
  } catch (e) {
    return null;
  }
}

function stripQuery(url) {
  return String(url || '').replace(/[?#].*$/, '');
}

function safeNumber(fn) {
  try {
    const v = fn();
    return typeof v === 'number' && !Number.isNaN(v) ? Math.round(v) : null;
  } catch (e) {
    return null;
  }
}

// Did THIS attempt leave the page? Resource Timing keeps an entry per request the browser
// actually issued (current Chrome records failed ones too, zero-sized). Entries are keyed by
// URL, and the boot fan-out and list screens re-request the same URLs, so an entry only counts
// when it started after this attempt did. Without a start time an older entry cannot be told
// apart from this one: unknown (null), never a false "sent".
function attemptWasSent(perf, url, startedAt) {
  try {
    if (!perf || typeof perf.getEntriesByName !== 'function') return null;
    const entries = perf.getEntriesByName(String(url || '')) || [];
    if (typeof startedAt === 'number') {
      return entries.some(e => e && typeof e.startTime === 'number'
        && e.startTime >= startedAt - START_TOLERANCE_MS);
    }
    return entries.length > 0 ? null : false;
  } catch (e) {
    return null;
  }
}

export function describeNetworkFailure(request, win) {
  const w = win || (typeof window !== 'undefined' ? window : {});
  const doc = w.document || {};
  const nav = w.navigator || {};
  const perf = w.performance;
  const req = request || {};
  const tokenHeaderBytes = typeof req.tokenHeaderBytes === 'number' ? req.tokenHeaderBytes : 0;
  let cookieBytes = null;
  try {
    cookieBytes = typeof doc.cookie === 'string' ? doc.cookie.length : null;
  } catch (e) {
    cookieBytes = null;
  }
  const navEntry = navigationEntry(perf);
  return {
    url: stripQuery(req.url),
    method: String(req.method || 'GET').toUpperCase(),
    retry: !!req.retry,
    // Decided from the rejection's ORIGINAL message, before any catch rewrites it: fetch()
    // also rejects for invalid request options or an abort, and those are real errors that
    // must keep reaching Bugsnag untouched. This flag is what the noise filter keys on.
    networkFailure: isNetworkFailureMessage(req.message),
    msSincePageStart: safeNumber(() => perf.now()),
    readyState: doc.readyState || null,
    // At failure time; the noise filter records the same two at notify time, and a page that
    // went hidden in between is itself a clue.
    visibilityAtFailure: doc.visibilityState || null,
    onlineAtFailure: typeof nav.onLine === 'boolean' ? nav.onLine : null,
    // Chrome boots a prerendered page in the background before the user commits to it and
    // discards it — killing its in-flight fetches — if they never do. `prerendering` is true
    // while that lasts; a non-zero activationStart says the page WAS prerendered.
    prerendering: typeof doc.prerendering === 'boolean' ? doc.prerendering : null,
    activationStart: safeNumber(() => navEntry.activationStart),
    wasSent: attemptWasSent(perf, req.url, req.startedAt),
    tokenHeaderBytes,
    cookieBytes,
    bigHeaderLine: Math.max(tokenHeaderBytes, cookieBytes || 0) >= BIG_HEADER_LINE_BYTES,
  };
}

export function attachNetworkDiagnostics(error, request, win) {
  if (!error || typeof error !== 'object') return error;
  try {
    Object.assign(error, { mhubNetwork: describeNetworkFailure(request, win) });
  } catch (e) {
    // diagnostics are a passenger on the real error, never a second failure
  }
  return error;
}



// WEBPACK FOOTER //
// ./src/utils/networkDiagnostics.js